import Paper, { type PaperProps } from '@mui/material/Paper';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export const DRAGGABLE_DIALOG_TITLE_ID = 'draggable-dialog-title';

/** Minimum visible edge (px) so the dialog can always be grabbed again. */
const MIN_VISIBLE_EDGE = 56;

const clampOffsetToViewport = (
  paper: HTMLElement,
  currentOffset: { x: number; y: number },
  nextOffset: { x: number; y: number }
): { x: number; y: number } => {
  const rect = paper.getBoundingClientRect();
  const deltaX = nextOffset.x - currentOffset.x;
  const deltaY = nextOffset.y - currentOffset.y;

  let left = rect.left + deltaX;
  let top = rect.top + deltaY;
  let right = rect.right + deltaX;
  let bottom = rect.bottom + deltaY;

  let { x, y } = nextOffset;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (left > viewportWidth - MIN_VISIBLE_EDGE) {
    x -= left - (viewportWidth - MIN_VISIBLE_EDGE);
    left = viewportWidth - MIN_VISIBLE_EDGE;
    right = left + rect.width;
  }
  if (right < MIN_VISIBLE_EDGE) {
    x += MIN_VISIBLE_EDGE - right;
  }
  if (top > viewportHeight - MIN_VISIBLE_EDGE) {
    y -= top - (viewportHeight - MIN_VISIBLE_EDGE);
    top = viewportHeight - MIN_VISIBLE_EDGE;
    bottom = top + rect.height;
  }
  if (bottom < MIN_VISIBLE_EDGE) {
    y += MIN_VISIBLE_EDGE - bottom;
  }

  return { x, y };
};

/**
 * MUI Dialog Paper that can be dragged by `#draggable-dialog-title`
 * and resized from the bottom-right corner (CSS resize).
 */
export const DraggableResizablePaper = (props: PaperProps) => {
  const { sx, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, ...rest } = props;
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    pointerX: number;
    pointerY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    onPointerDown?.(event);
    if (event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target?.closest(`#${DRAGGABLE_DIALOG_TITLE_ID}`)) {
      return;
    }

    dragRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    onPointerMove?.(event);
    if (!dragRef.current) {
      return;
    }

    const proposed = {
      x: dragRef.current.originX + (event.clientX - dragRef.current.pointerX),
      y: dragRef.current.originY + (event.clientY - dragRef.current.pointerY),
    };

    const paper = paperRef.current;
    setOffset((current) => (paper ? clampOffsetToViewport(paper, current, proposed) : proposed));
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    onPointerUp?.(event);
    if (dragRef.current) {
      dragRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  };

  const cancelDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    onPointerCancel?.(event);
    dragRef.current = null;
  };

  return (
    <Paper
      {...rest}
      ref={paperRef}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={cancelDrag}
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          resize: 'both',
          overflow: 'auto',
          minWidth: 420,
          minHeight: 320,
          maxWidth: '96vw',
          maxHeight: '92vh',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};
